import { Controller } from "@hotwired/stimulus"
import { savePlan } from "plan_storage"
import { preserveScroll } from "controllers/scroll_preserve"
import { renderIcons } from "lucide_icons"

// Goal reorder mini-view — ports the legacy panel at
// editor_source/pcp_builder.html#6596-6605. Renders a compact
// two-column list of goal mini-cards above the full Goals editor.
// Each card:
//   - shows the goal number, truncated description, and resource
//     type pills (RC = green, Generic = blue, Unmet = amber)
//   - is draggable: drop on another card to reorder plan.goals
//   - has a "scroll to goal" arrow on hover that smooth-scrolls the
//     full goal card into view
// Hides itself when there's only one goal.

const STORAGE_KEY = "pcp_plan_data"
const SNIPPET_MAX = 50

export default class extends Controller {
  static targets = ["panel", "list"]

  connect() {
    this.plan = this._load()
    this._render()
    this._onChange = (e) => {
      if (this._suppressNextChange) { this._suppressNextChange = false; return }
      const p = e.detail?.path || ""
      if (p === "goals" || p.startsWith("goals.") || p === "*" ||
          p === "meta.redacted" || p === "client.firstName" || p === "client.name") {
        this.plan = e.detail.plan || this._load()
        this._render()
      }
    }
    document.addEventListener("plan:change", this._onChange)
  }

  disconnect() {
    document.removeEventListener("plan:change", this._onChange)
  }

  // ----- drag handlers -----
  dragStart(event) {
    const idx = event.currentTarget.dataset.goalIdx
    if (idx == null) return
    this._dragIdx = parseInt(idx, 10)
    event.dataTransfer.effectAllowed = "move"
    try { event.dataTransfer.setData("text/plain", String(idx)) } catch (_) {}
    event.currentTarget.classList.add("is-dragging")
  }
  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    event.currentTarget.classList.add("is-drop-target")
  }
  dragLeave(event) {
    event.currentTarget.classList.remove("is-drop-target")
  }
  drop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("is-drop-target")
    const targetIdx = parseInt(event.currentTarget.dataset.goalIdx, 10)
    if (Number.isNaN(targetIdx) || this._dragIdx == null || targetIdx === this._dragIdx) return
    const goals = this.plan.goals || []
    if (this._dragIdx < 0 || this._dragIdx >= goals.length) return
    const [moved] = goals.splice(this._dragIdx, 1)
    goals.splice(targetIdx, 0, moved)
    this.plan.goals = goals
    this._save()
    this._render()
  }
  dragEnd(event) {
    this._dragIdx = null
    this.listTarget?.querySelectorAll(".is-dragging, .is-drop-target")
      .forEach(el => el.classList.remove("is-dragging", "is-drop-target"))
  }

  // ----- scroll-to-goal arrow -----
  scrollTo(event) {
    event.stopPropagation()
    event.preventDefault()
    const idx = event.currentTarget.dataset.goalIdx
    if (idx == null) return
    const target = document.querySelector(`[data-row][data-goal-idx="${idx}"], [data-row]:nth-of-type(${parseInt(idx, 10) + 1})`)
                || document.querySelectorAll(`[data-controller~="dynamic-list"][data-dynamic-list-path-value="goals"] [data-row]`)[parseInt(idx, 10)]
    if (target) target.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  // Resolve [FirstName] in a goal snippet for display: real first name when
  // Redact PHI is OFF, the literal tag when ON (matches the PDF + editor).
  // Returns a new string — never mutates the goal's stored description.
  _resolveName(str) {
    if (!str || !/\[First\s*Name\]/i.test(str)) return str
    if (this.plan?.meta?.redacted) return str
    const c = this.plan?.client || {}
    const name = (c.firstName && c.firstName.trim()) ||
                 (c.name && c.name.trim() && c.name.trim().split(/\s+/)[0]) || ""
    return name ? str.replace(/\[First\s*Name\]/gi, name) : str
  }

  // ----- rendering -----
  _render() {
    if (!this.hasPanelTarget || !this.hasListTarget) return
    preserveScroll(this.element, () => {
      const goals = this.plan.goals || []
      if (goals.length < 2) {
        this.panelTarget.hidden = true
        this.listTarget.innerHTML = ""
        return
      }
      this.panelTarget.hidden = false
      const half = Math.ceil(goals.length / 2)
      const items = goals.map((g, gi) => this._renderItem(g, gi))
      this.listTarget.innerHTML = `
        <div class="gr-col">${items.slice(0, half).join("")}</div>
        <div class="gr-col">${items.slice(half).join("")}</div>`
      renderIcons(this.element)
    })
  }

  _renderItem(g, idx) {
    // Resolve [FirstName] for DISPLAY only (snippet + hover title). The stored
    // goal.description keeps its raw token — g is never mutated here.
    const desc = this._resolveName((g.description || g.heading || "Untitled Goal").trim())
    const snippet = desc.length > SNIPPET_MAX ? desc.slice(0, SNIPPET_MAX) + "…" : desc
    const resources = (g.resources || []).map(r => {
      // "Other" services show the specified name (r.comment). If that's blank
      // but the resource carries a real service in text (imported / AI data
      // that left type="Other"), show that instead of the literal "Other".
      const svc  = (r.type || r.text || "").trim()
      const spec = (r.comment || "").trim()
      const alt  = (r.text || "").trim()
      let name = svc
      if (svc === "Other" || !svc) {
        name = spec || (alt && alt !== "Other" ? alt : "") || "Other"
      }
      const tag = r.tag === "rc" ? "rc" : r.tag === "unmet" ? "unmet" : "general"
      return `<span class="gr-pill gr-pill-${tag}">${this._esc(name)}</span>`
    }).join("")
    return `
      <div class="gr-item"
           draggable="true"
           data-goal-idx="${idx}"
           data-action="dragstart->goal-reorder#dragStart dragover->goal-reorder#dragOver dragleave->goal-reorder#dragLeave drop->goal-reorder#drop dragend->goal-reorder#dragEnd">
        <div class="gr-row">
          <i data-lucide="grip-vertical" class="gr-grip"></i>
          <span class="gr-num">${idx + 1}.</span>
          <span class="gr-snippet" title="${this._esc(desc)}">${this._esc(snippet)}</span>
          <button type="button"
                  class="gr-scroll"
                  data-action="goal-reorder#scrollTo"
                  data-goal-idx="${idx}"
                  title="Scroll to goal">
            <i data-lucide="arrow-right"></i>
          </button>
        </div>
        ${resources ? `<div class="gr-pills">${resources}</div>` : ""}
      </div>`
  }

  _esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
  _load() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") }
    catch (_) { return {} }
  }
  _save() {
    savePlan(this.plan)
    this._suppressNextChange = true
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: "goals", plan: this.plan, source: "goal-reorder" }
    }))
  }
}
